import { CheckCircle, Loader2, UserPlus, XCircle } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { AuthLayout } from '@/components/layout/AuthLayout'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { routePaths } from '@/lib/router'
import { invitationService } from '@/services/invitations'

type InvitationState =
  | 'loading'
  | 'success'
  | 'signup-required'
  | 'error'
  | 'invalid-token'
  | 'expired-token'

export function AcceptInvitationPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [invitationState, setInvitationState] =
    useState<InvitationState>('loading')
  const [errorMessage, setErrorMessage] = useState<string>('')

  const hasAttemptedRef = useRef(false)
  const lastTokenRef = useRef<string | null>(null)

  const token = searchParams.get('token')

  if (token !== lastTokenRef.current) {
    lastTokenRef.current = token
    hasAttemptedRef.current = false
  }

  const acceptInvitation = useCallback(async (invitationToken: string) => {
    if (hasAttemptedRef.current) return
    hasAttemptedRef.current = true

    try {
      setInvitationState('loading')
      const response = await invitationService.acceptInvitation(invitationToken)

      if (response.signup_required) {
        setInvitationState('signup-required')
      } else {
        setInvitationState('success')
      }
    } catch (error) {
      const errorMsg =
        error instanceof Error ? error.message : 'Failed to accept invitation'
      setErrorMessage(errorMsg)

      // Determine specific error type based on message
      if (errorMsg.toLowerCase().includes('expired')) {
        setInvitationState('expired-token')
      } else if (
        errorMsg.toLowerCase().includes('invalid') ||
        errorMsg.toLowerCase().includes('not found')
      ) {
        setInvitationState('invalid-token')
      } else {
        setInvitationState('error')
      }
    }
  }, [])

  const getIcon = () => {
    switch (invitationState) {
      case 'loading':
        return <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      case 'success':
        return <CheckCircle className="w-8 h-8 text-green-600" />
      case 'signup-required':
        return <UserPlus className="w-8 h-8 text-blue-600" />
      case 'error':
      case 'invalid-token':
      case 'expired-token':
        return <XCircle className="w-8 h-8 text-red-600" />
      default:
        return <UserPlus className="w-8 h-8 text-orange-600" />
    }
  }

  const getTitle = () => {
    switch (invitationState) {
      case 'loading':
        return 'Processing Invitation...'
      case 'success':
        return 'Welcome to the Team!'
      case 'signup-required':
        return 'Complete Your Registration'
      case 'invalid-token':
        return 'Invalid Invitation Link'
      case 'expired-token':
        return 'Invitation Link Expired'
      case 'error':
        return 'Invitation Failed'
      default:
        return 'Team Invitation'
    }
  }

  const getMessage = () => {
    switch (invitationState) {
      case 'loading':
        return 'Please wait while we process your team invitation...'
      case 'success':
        return 'You have successfully joined the team! You can now access all features and collaborate with your teammates.'
      case 'signup-required':
        return 'To join the team, please complete your account registration. Your invitation will be automatically accepted after signup.'
      case 'invalid-token':
        return 'The invitation link you clicked is invalid or has already been used. Please request a new invitation from your team administrator.'
      case 'expired-token':
        return 'This invitation link has expired for security reasons. Please request a new invitation from your team administrator.'
      case 'error':
        return (
          errorMessage ||
          'An error occurred while processing your invitation. Please try again or contact your team administrator.'
        )
      default:
        return 'Something went wrong while processing your invitation.'
    }
  }

  const getBackgroundColor = () => {
    switch (invitationState) {
      case 'success':
        return 'bg-green-100 dark:bg-green-900/20'
      case 'loading':
        return 'bg-blue-100 dark:bg-blue-900/20'
      case 'signup-required':
        return 'bg-blue-100 dark:bg-blue-900/20'
      case 'error':
      case 'invalid-token':
      case 'expired-token':
        return 'bg-red-100 dark:bg-red-900/20'
      default:
        return 'bg-orange-100 dark:bg-orange-900/20'
    }
  }

  useEffect(() => {
    if (!token) {
      setInvitationState('invalid-token')
      return
    }

    acceptInvitation(token)
  }, [token, acceptInvitation])

  return (
    <AuthLayout
      title="Team Invitation"
      subtitle="Join your team"
      showBranding={false}
    >
      <Card className="w-full">
        <CardHeader className="text-center">
          <CardTitle className="flex items-center justify-center space-x-2">
            <UserPlus className="h-5 w-5" />
            <span>Team Invitation</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Status Icon and Message */}
          <div className="text-center space-y-4">
            <div
              className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center ${getBackgroundColor()}`}
            >
              {getIcon()}
            </div>

            <div className="space-y-2">
              <h3 className="text-xl font-semibold">{getTitle()}</h3>
              <p className="text-muted-foreground">{getMessage()}</p>
            </div>
          </div>

          {/* Success Actions */}
          {invitationState === 'success' && (
            <div className="space-y-4">
              <Alert className="bg-green-50 border-green-200 dark:bg-green-900/10 dark:border-green-800">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <div className="text-green-800 dark:text-green-200">
                  <p className="font-medium">You're now part of the team!</p>
                  <p className="text-sm mt-1">
                    Start collaborating with your teammates and access all
                    available features.
                  </p>
                </div>
              </Alert>

              <Button
                onClick={() => navigate(routePaths.dashboard)}
                className="w-full"
                size="lg"
              >
                Go to Dashboard
              </Button>
            </div>
          )}

          {/* Signup Required Actions */}
          {invitationState === 'signup-required' && (
            <div className="space-y-4">
              <Alert className="bg-blue-50 border-blue-200 dark:bg-blue-900/10 dark:border-blue-800">
                <UserPlus className="h-4 w-4 text-blue-600" />
                <div className="text-blue-800 dark:text-blue-200">
                  <p className="font-medium">Account Registration Required</p>
                  <p className="text-sm mt-1">
                    Create your account to join the team. Your invitation will
                    be automatically accepted.
                  </p>
                </div>
              </Alert>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Button
                  onClick={() =>
                    navigate(`${routePaths.register}?invitation_token=${token}`)
                  }
                  className="w-full"
                  size="lg"
                >
                  Create Account
                </Button>

                <Button
                  variant="outline"
                  onClick={() =>
                    navigate(
                      `${routePaths.login}?redirect=${encodeURIComponent(`/accept-invitation?token=${token}`)}`
                    )
                  }
                  className="w-full"
                  size="lg"
                >
                  I Have Account
                </Button>
              </div>
            </div>
          )}

          {/* Error Actions */}
          {(invitationState === 'expired-token' ||
            invitationState === 'invalid-token' ||
            invitationState === 'error') && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Button
                  variant="outline"
                  onClick={() => navigate(routePaths.register)}
                  className="w-full"
                >
                  Create Account
                </Button>

                <Button
                  onClick={() => navigate(routePaths.login)}
                  className="w-full"
                >
                  Go to Login
                </Button>
              </div>
            </div>
          )}

          {/* Loading State */}
          {invitationState === 'loading' && (
            <div className="flex justify-center">
              <Button variant="outline" disabled>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </Button>
            </div>
          )}

          {/* Help Section */}
          <div className="border-t pt-6">
            <div className="text-center space-y-3">
              <p className="text-sm text-muted-foreground">
                Need help with your invitation?
              </p>
              <div className="flex flex-col sm:flex-row gap-2 text-sm">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-primary hover:underline"
                >
                  Contact Team Admin
                </Button>
                <span className="hidden sm:inline text-muted-foreground">
                  •
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-primary hover:underline"
                >
                  Support
                </Button>
              </div>
            </div>
          </div>

          {/* Return to Registration */}
          <div className="text-center">
            <p className="text-sm text-muted-foreground">
              Don't have an account?{' '}
              <Link
                to={routePaths.register}
                className="text-primary hover:underline font-medium"
              >
                Create account
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </AuthLayout>
  )
}
